<template>
  <div class="search-bar">
    <div
      class="search-bar-inner flex items-center justify-between"
      target=".page"
      :class="{ 'full-width': fullWidth }"
    >
      <div class="search-bar-left flex items-center">
        <y-location
          :showlocation="showHoteluxLocation"
          :show-label="showLabel"
          :model-value="hotelSearchModel.location"
          :location-disabled="locationDisabled"
          @update:model-value="handleOnChangeLocation"
        />
        <el-divider direction="vertical" v-if="showHoteluxLocation" />
        <y-datepicker
          :show-label="showLabel"
          v-model="searchModel.stay.date"
          @change="handleOnChangeDate"
        />
        <el-divider direction="vertical" />
        <y-travelers
          :show-label="showLabel"
          v-model="searchModel.stay.guest"
          @change="handleOnChangeGuest"
        />
      </div>

      <y-button class="btn uppercase" type="primary" @click="handleClickSearch">
        <y-text font="var(--y-bold-font)" size="16px" color="#fff">{{
          t("shell.common.search")
        }}</y-text>
      </y-button>
    </div>
  </div>
</template>

<script setup>
import YDatepicker from "@packages/booking/hotel/Filter/Datepicker.vue";
import YLocation from "@packages/booking/hotel/Filter/Location.vue";
import YTravelers from "@packages/booking/hotel/Filter/Travelers.vue";
import dayjs from "dayjs";
import { ElMessage } from "element-plus";
import _ from "lodash";
import { storeToRefs } from "pinia";
import { ref } from "vue";
import { useI18n } from "vue-i18n";

import { useHotelStoreHook } from "../../store/hotel";

const props = defineProps({
  affixOffset: {
    type: Number,
    default: 0
  },
  fullWidth: {
    type: Boolean,
    default: false
  },
  showLabel: {
    type: Boolean,
    default: true
  },
  locationDisabled: {
    type: Boolean,
    default: false
  },
  locationRefIgnored: {
    type: Boolean,
    default: false
  },
  showHoteluxLocation: {
    type: Boolean,
    default: true
  }
});
const emit = defineEmits(["visible-change", "search-change", "search-click"]);

const { t } = useI18n();
const YHotelStore = useHotelStoreHook();

const { searchModel, hotelSearchModel } = storeToRefs(YHotelStore);
const _location = ref({});

const performSearch = () => {
  const _locationRef = _.get(hotelSearchModel.value, "locationRef", "");
  if (_locationRef || props.locationRefIgnored) {
    emit("search-change");
  }
};

const debouncedSearch = _.debounce(performSearch, 300);

const instantSearch = () => {
  debouncedSearch.cancel();
  const _locationRef = _.get(hotelSearchModel.value, "locationRef", "");
  if (!_locationRef && !props.locationRefIgnored) {
    ElMessage.error(t("shared.hotel.search.locationRequired"));
    return;
  }
  performSearch();
};
const handleOnChangeLocation = newLocation => {
  YHotelStore.hotelSearchModel.location = newLocation;

  if (newLocation) {
    YHotelStore.hotelSearchModel.locationRef = newLocation._id;
    YHotelStore.hotelSearchModel.locationType = newLocation.locationType;
  } else {
    YHotelStore.hotelSearchModel.locationRef = "";
    YHotelStore.hotelSearchModel.locationType = "";
    YHotelStore.hotelSearchModel.filter = {};
    debouncedSearch();
    return;
  }

  const { locationType, _id, showLabel } = newLocation;
  let newFilter = {};

  switch (locationType) {
    case "city":
      newFilter = { city: { _id } };
      break;
    case "moduleTpHotel":
      newFilter = { hotels: [{ _id }] };
      YHotelStore.selectedHotel = newLocation; // 仍然需要设置 selectedHotel
      break;
    case "googleMapPolitical":
      newFilter = {
        _biz: { googleMapPolitical: { place_id: _id, description: showLabel } }
      };
      break;
    case "search":
      newFilter = { search: showLabel };
      break;
  }
  YHotelStore.hotelSearchModel.filter = newFilter;

  // 3. 决定是立即搜索还是防抖搜索
  if (locationType === "moduleTpHotel") {
    instantSearch();
  } else {
    debouncedSearch();
  }
};

const handleOnChangeDate = date => {
  YHotelStore.searchModel.stay.date = {
    checkIn: dayjs(date[0]).format("YYYY-MM-DD"),
    checkOut: dayjs(date[1]).format("YYYY-MM-DD")
  };

  debouncedSearch();
};

const handleOnChangeGuest = guest => {
  YHotelStore.searchModel.stay.guest = guest;

  debouncedSearch();
};

const handleClickSearch = async () => {
  const _locationRef = _.get(hotelSearchModel.value, "locationRef", "");
  if (!_locationRef && !props.locationRefIgnored) {
    ElMessage.error(t("shared.hotel.search.locationRequired"));
    return;
  }
  emit("search-click");
};

defineOptions({
  name: "SearchBar"
});
</script>

<style lang="scss" scoped>
.search-bar {
  .el-divider--vertical {
    margin: 0 32px;
    height: 50px;
    border-color: #eee;
  }

  .search-bar-inner {
    width: var(--search-bar-inner-width, 1080px);
    margin: 0 auto;
    background: #fff;
    padding: 12px 32px 10px;
    &.full-width {
      width: 100%;
      padding: 12px 0 10px;
    }
    .btn {
      padding: 0 48px;
      height: 48px;
    }

    .btn:hover {
      transform: scale(1.03);
      transition: transform 0.3s ease-in-out;
      background-color: #000;
    }
  }
}
</style>
